import type { NavigationGuardWithThis } from 'vue-router'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import { useUserStore } from '@/features/user/stores/user.store'
import { API_CONFIG } from '@/config/api.config'
import { toAppPath } from '@/utils/app-url'

function redirectToOidc(routerPath: string): false {
  // `routerPath` comes from Vue Router and excludes the deploy base, but the
  // returnUrl is resolved by the browser against the origin — prefix the base
  // so login returns into the SPA and not the V2 MVC app.
  const returnUrl = toAppPath(routerPath)
  // When the SPA is served from a different origin than the WebApp, OIDC login
  // must target the WebApp's absolute origin and carry an absolute returnUrl so
  // it can send us back here. When same-origin (baseUrl empty — local dev and
  // a sub-path deploy) keep the relative behaviour.
  const base = API_CONFIG.baseUrl
  const target = base
    ? `${base}/Account/Login?returnUrl=${encodeURIComponent(window.location.origin + returnUrl)}`
    : `/Account/Login?returnUrl=${encodeURIComponent(returnUrl)}`
  window.location.href = target
  return false
}

export const authGuard: NavigationGuardWithThis<undefined> = async (to) => {
  const auth = useAuthStore()

  if (!auth.sessionChecked) {
    const valid = await auth.checkSession()
    if (!valid && to.meta.requiresAuth !== false) {
      return redirectToOidc(to.fullPath)
    }
  }

  if (to.meta.requiresAuth !== false && !auth.isAuthenticated) {
    return redirectToOidc(to.fullPath)
  }

  if (to.name === 'Login' && auth.isAuthenticated) {
    return { name: 'Dashboard' }
  }

  // First-login gate: force new users through the onboarding questionnaire until
  // they have a saved response. Only fires for authenticated app routes, never
  // for the questionnaire itself (would loop) or public/auth routes. BetaAccess is
  // exempt too: a new, blocked user would otherwise loop between the questionnaire
  // and the new-UI beta gate (experience.guard.ts).
  if (
    auth.isAuthenticated &&
    to.meta.requiresAuth !== false &&
    to.name !== 'OnboardingQuestionnaire' &&
    to.name !== 'BetaAccess'
  ) {
    const shouldOnboard = await needsOnboarding()
    if (shouldOnboard) {
      return { name: 'OnboardingQuestionnaire' }
    }
  }

  return true
}

/**
 * Decides whether the current user must be sent to the onboarding questionnaire.
 * Loads the profile on demand (the guard runs before DefaultLayout's bootstrap).
 * Fails open — a fetch error never traps the user out of the app. Impersonated
 * sessions and admins are exempt so support flows aren't hijacked.
 */
async function needsOnboarding(): Promise<boolean> {
  const user = useUserStore()
  if (!user.profile) {
    try {
      await user.fetchProfile()
    } catch {
      return false
    }
  }
  const profile = user.profile
  if (!profile) return false
  if (profile.Impersonated || profile.IsAppAdmin) return false
  // Strictly `=== false`: undefined (older payload / fetch miss) never forces onboarding.
  return profile.HasCompletedQuestionnaire === false
}
